/**
 * @typedef {Object} CommandExecutionLog
 * @property {string} id
 * @property {string} level
 * @property {string} message
 * @property {string} createdAt
 */

/**
 * @typedef {Object} CommandExecutionLogService
 * @property {Object} filters
 * @property {function(): angular.IPromise<CommandExecutionLog[]>} get
 * @property {function(): angular.IPromise<CommandExecutionLog[]>} next
 * @property {function(): void} reset
 * @property {function(): boolean} hasNextPage
 */

const mod = require('../bahabot.module.js');

/**
 * @param {angular.resource.IResourceService} $resource
 * @returns {CommandExecutionLogService}
 */
function bahabotCommandExecutionLogFactory($resource) {
    const CommandExecutionLog = $resource('/api/protected/command-execution-logs', {}, {
        fetch: { method: 'GET', isArray: false },
    });

    const self = {};

    // Public input (bound by controller via ng-model)
    self.filters = {
        executionId: '',
    };

    // Private cursor tracking
    self.lastFilters = null;

    self.data = { items: [], pageInfo: {} };
    self.loading = false;
    self.loadingMore = false;

    /**
     * Load first page (uses current public filters)
     */
    self.fetch = () => {
        self.loading = true;

        const query = {
            executionId: self.filters.executionId,
        };

        return CommandExecutionLog
            .fetch(query)
            .$promise
            .then(res => {
                self.data = res.data;
                self.lastFilters = {
                    executionId: self.filters.executionId,
                    cursor: res.data.pageInfo.endCursor,
                };
                return res.data.items;
            })
            .finally(() => {
                self.loading = false;
            });
    };

    /**
     * Load next page based on lastFilters
     */
    self.next = () => {
        if (!self.hasNextPage()) return Promise.resolve([]);

        self.loadingMore = true;

        const query = {
            executionId: self.lastFilters.executionId,
            cursor: self.lastFilters.cursor,
        };

        return CommandExecutionLog
            .fetch(query)
            .$promise
            .then(res => {
                self.data.items = self.data.items.concat(res.data.items);
                self.data.pageInfo = res.data.pageInfo;
                self.lastFilters.cursor = res.data.pageInfo.endCursor;
                return res.data.items;
            })
            .finally(() => {
                self.loadingMore = false;
            });
    };

    /**
     * Reset all filters and pagination state
     */
    self.reset = () => {
        self.filters.executionId = '';
        self.lastFilters = null;
        self.data = { items: [], pageInfo: {} };
    };

    /**
     * Indicates if another page is available
     */
    self.hasNextPage = () => {
        return !!self.data.pageInfo?.hasNextPage;
    };

    return self;
}

mod.factory('CommandExecutionLogService', ["$resource", bahabotCommandExecutionLogFactory]);
